import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Pencil, Plus, RefreshCw, Trash2, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { applyEmployeesMasterList, setState, useHR, type Employee } from "@/lib/hr-store";
import { createEmployee, deleteEmployee, fetchEmployees, updateEmployee, type EmployeeRow } from "@/lib/employees-api";
import { getAccessToken } from "@/lib/local-auth";
import { toast } from "sonner";

export const Route = createFileRoute("/karyawan")({
  component: KaryawanPage,
});

const empty: Employee = {
  id: "",
  name: "",
  position: "",
  department: "",
};

function mapRowsToEmployees(rows: EmployeeRow[]): Employee[] {
  return rows.map((r) => ({
    id: r.id,
    name: r.name,
    department: r.department,
    position: r.role,
  }));
}

function KaryawanPage() {
  const employees = useHR((s) => s.employees);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Employee>(empty);
  const editing = !!draft.id;
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  /** Setelah muat dari server sukses (dengan token), respons berisi daftar kosong. */
  const [loadedEmptyFromApi, setLoadedEmptyFromApi] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadFromApi = useCallback(async () => {
    if (!getAccessToken()) {
      setListError(null);
      setLoadedEmptyFromApi(false);
      return;
    }
    setListLoading(true);
    setListError(null);
    setLoadedEmptyFromApi(false);
    try {
      const rows = await fetchEmployees();
      applyEmployeesMasterList(mapRowsToEmployees(rows));
      setLoadedEmptyFromApi(rows.length === 0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Gagal memuat dari server";
      setListError(msg);
      setLoadedEmptyFromApi(false);
      toast.error(msg);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFromApi();
  }, [loadFromApi]);

  const save = async () => {
    if (!draft.name?.trim() || !draft.position?.trim()) {
      toast.error("Nama & jabatan wajib diisi");
      return;
    }
    if (!getAccessToken()) {
      toast.error("Login terlebih dahulu untuk menyimpan ke server.");
      return;
    }
    setSaveLoading(true);
    try {
      if (editing) {
        await updateEmployee(draft.id, {
          name: draft.name.trim(),
          department: draft.department.trim(),
          role: draft.position.trim(),
        });
        toast.success("Karyawan diperbarui");
      } else {
        await createEmployee({
          name: draft.name.trim(),
          department: draft.department.trim(),
          role: draft.position.trim(),
        });
        toast.success("Karyawan ditambahkan");
      }
      await loadFromApi();
      setOpen(false);
      setDraft(empty);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menyimpan karyawan");
    } finally {
      setSaveLoading(false);
    }
  };

  const remove = async (id: string) => {
    if (!getAccessToken()) {
      toast.error("Login terlebih dahulu untuk menghapus data di server.");
      return;
    }
    setDeleteLoading(true);
    try {
      await deleteEmployee(id);
      await loadFromApi();
      toast.success("Karyawan dihapus");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menghapus karyawan");
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const hasToken = !!getAccessToken();
  const showEmptyState = !listLoading && employees.length === 0;
  const emptyMessage = (() => {
    if (listError) {
      return {
        title: "Tidak dapat menampilkan data",
        body: "Periksa pesan merah di atas, pastikan backend & Supabase berjalan, lalu klik Muat dari server.",
      };
    }
    if (!hasToken) {
      return {
        title: "Belum terhubung ke server",
        body: "Login terlebih dahulu untuk mengambil daftar karyawan dari database. Tanpa login, data di halaman ini tidak disinkronkan dengan server.",
      };
    }
    if (loadedEmptyFromApi) {
      return {
        title: "Belum ada karyawan",
        body: "Belum ada karyawan tercatat untuk akun Anda. Gunakan Tambah Karyawan untuk menambahkan entri pertama.",
      };
    }
    return {
      title: "Daftar kosong",
      body: "Klik Muat dari server untuk mengambil data terbaru dari database, atau gunakan Tambah Karyawan. Jika Anda baru menghapus semua baris di halaman ini, muat ulang untuk mengisi ulang dari server.",
    };
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Karyawan</h1>
          <p className="mt-1.5 max-w-xl text-sm leading-snug text-muted-foreground">
            Muat dari server, lalu tambah atau ubah nama, jabatan, dan departemen — dipakai agar nama di penggajian cocok dengan master karyawan.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={listLoading || !getAccessToken()} onClick={() => void loadFromApi()}>
            <RefreshCw className={`h-4 w-4 mr-2 ${listLoading ? "animate-spin" : ""}`} />
            Muat dari server
          </Button>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setDraft(empty); }}>
          <DialogTrigger asChild>
            <Button onClick={() => setDraft(empty)}>
              <Plus className="h-4 w-4 mr-2" /> Tambah Karyawan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit" : "Tambah"} Karyawan</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nama" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} className="col-span-2" />
              <Field label="Jabatan" value={draft.position} onChange={(v) => setDraft({ ...draft, position: v })} />
              <Field label="Departemen" value={draft.department} onChange={(v) => setDraft({ ...draft, department: v })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saveLoading}>
                Batal
              </Button>
              <Button onClick={() => void save()} disabled={saveLoading}>
                {saveLoading ? "Menyimpan…" : "Simpan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {listError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{listError}</div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Daftar Karyawan</CardTitle></CardHeader>
        <CardContent>
          {listLoading && employees.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <RefreshCw className="h-8 w-8 animate-spin opacity-70" aria-hidden />
              <p>Memuat daftar dari server…</p>
            </div>
          ) : showEmptyState ? (
            <div
              role="status"
              className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/30 px-6 py-10 text-center"
            >
              <div className="rounded-full bg-muted p-3">
                <Users className="h-8 w-8 text-muted-foreground" aria-hidden />
              </div>
              <div className="space-y-1 max-w-md">
                <p className="font-medium text-foreground">{emptyMessage.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{emptyMessage.body}</p>
              </div>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-3 font-medium">Nama</th>
                  <th className="py-3 font-medium">Jabatan</th>
                  <th className="py-3 font-medium">Departemen</th>
                  <th className="py-3 font-medium text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-3 font-medium">{e.name}</td>
                    <td className="py-3">{e.position}</td>
                    <td className="py-3">{e.department}</td>
                    <td className="py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => { setDraft(e); setOpen(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(e)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(next) => { if (!next) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus karyawan?</AlertDialogTitle>
            <AlertDialogDescription>
              Data karyawan{deleteTarget ? ` “${deleteTarget.name}”` : ""} akan dihapus dari server. Tindakan ini tidak bisa dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Batal</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteLoading}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) void remove(deleteTarget.id);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? "Menghapus…" : "Hapus"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", className = "" }: { label: string; value: string; onChange: (v: string) => void; type?: string; className?: string }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-xs">{label}</Label>
      <Input value={value} type={type} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
